/**
 * @fileoverview Verification Page for the verifying entity step of the Listings Launch flow.
 *
 * @author Kuber Sethi (ksethi@yext.com)
 */

import {ExternalAccountsContext}
  from '/js/yext/externalaccounts/shared/listingslaunch/externalaccountscontext';
import ContentSkeletonCards from '/src/com/yext/listingslaunchstorm/js/components/common/contentskeletoncards';
import {Divider} from '/src/com/yext/listingslaunchstorm/js/components/common/divider';
import {EntityDetails} from '/src/com/yext/listingslaunchstorm/js/components/common/entitydetails';
import {ErrorCallout} from '/src/com/yext/listingslaunchstorm/js/components/common/errorcallout';
import {SubHeader} from '/src/com/yext/listingslaunchstorm/js/components/common/subheader';
import VerificationCodeEntry from '/src/com/yext/listingslaunchstorm/js/components/verification/verificationcodeentry';
import VerificationFlow from '/src/com/yext/listingslaunchstorm/js/components/verification/verificationflow';
import VerificationMethods from '/src/com/yext/listingslaunchstorm/js/components/verification/verificationmethods';
import {getIconForVerificationMethod} from '/src/com/yext/listingslaunchstorm/js/components/verification/verificationmethods';
import {ListingsLaunchContext} from '/src/com/yext/listingslaunchstorm/js/contexts/listingslaunchcontextprovider';
import {EntityInfo} from '/src/com/yext/listingslaunchstorm/js/data/entityinfo';
import {GetCompletedVerificationRequest} from '/src/com/yext/listingslaunchstorm/js/data/getcompletedverificationrequest';
import {GetVerificationMethodsRequest} from '/src/com/yext/listingslaunchstorm/js/data/getverificationmethodsrequest';
import {VerificationMethod, getVerificationMethodDisplayName} from '/src/com/yext/listingslaunchstorm/js/data/verificationmethod';
import {VerifiedListing} from '/src/com/yext/listingslaunchstorm/js/data/verifiedlisting';
import getCompletedVerification from '/src/com/yext/listingslaunchstorm/js/datasource/getcompletedverification';
import getVerificationMethods from '/src/com/yext/listingslaunchstorm/js/datasource/getverificationmethods';
import {createVerificationMethodToDestinationMap, getVerificationForEntity} from '/src/com/yext/listingslaunchstorm/js/util/listingslaunchtaskflowutil';
import {CalloutBanner, CalloutBannerType} from '/ui/components/calloutbanner/calloutbanner';
import {useIsSmallViewport} from '/ui/lib/responsivity';

import * as styles from '/src/com/yext/listingslaunchstorm/js/components/styles/verifyentity.module.scss';

/**
 * @param {{
 *  entity: !EntityInfo,
 * }} props
 * @return {React.ReactNode}
 */
const VerifyEntity = ({entity}) => {
  const [verificationMethodsResponse, setVerificationMethodsResponse] = React.useState(undefined);
  const [verificationMethodsLoaded, setVerificationMethodsLoaded] = React.useState(false);
  const [verificationDestinationMap, setVerificationDestinationMap] = React.useState(undefined);

  const [errorMessage, setErrorMessage] = React.useState('');
  const [showError, setShowError] = React.useState(false);

  const {matchedListings, verifiedListings} = React.useContext(ListingsLaunchContext);
  const {publisherIdToExternalAccount} = React.useContext(ExternalAccountsContext);
  const linkedAccount = publisherIdToExternalAccount.get(715);

  const matchedListing = matchedListings.find(m => m.entityId == entity.id);
  const verifiedListing = verifiedListings.find(v => v.entityId == entity.id);

  const [alreadyVerified, setAlreadyVerified] = React.useState(verifiedListing?.method === VerificationMethod.PREVIOUSLY_VERIFIED);
  // If the listing is already verified there's no need to fetch additional verification status data.
  const [loadedVerificationStatus, setLoadedVerificationStatus] = React.useState(alreadyVerified);

  const groupId = matchedListing.groupId;
  const externalId = matchedListing.externalId;

  const setVerificationDestination = (verificationMethod, verificationDestination) => {
    setVerificationDestinationMap(prev => {
      return {...prev, [verificationMethod]: verificationDestination};
    });
  };

  React.useEffect(() => {
    if (!alreadyVerified) {
      const abortController = new AbortController();
      // Check if the entity is already verified
      const request =
        new GetCompletedVerificationRequest({
          businessId: yext.businessId,
          entityId: entity.id,
          externalId: externalId,
          linkedAccountId: linkedAccount.id,
          groupId: groupId,
        });
      getCompletedVerification(request, abortController.signal)
        .then(response => {
          if (response.hasError) {
            setShowError(true);
            setErrorMessage(response.customErrorMessage ?? '');
          } else {
            const getCompletedVerificationResponse = response.value;
            setLoadedVerificationStatus(true);
            setAlreadyVerified(getCompletedVerificationResponse.verified);
          }
        }).catch(aborted => {
          if (!aborted) {
            console.log('Fetching completed verifications failed.');
          }
        });
      return () => abortController.abort();
    }
  }, [entity.id, externalId, linkedAccount, groupId, alreadyVerified, showError]);


  React.useEffect(() => {
    const abortController = new AbortController();
    if (loadedVerificationStatus && !verificationMethodsResponse) {
      const request =
        new GetVerificationMethodsRequest({
          businessId: yext.businessId,
          entityId: entity.id,
          externalId: externalId,
          groupId: groupId,
          linkedAccountId: linkedAccount.id,
        });
      getVerificationMethods(request, abortController.signal)
        .then(response => {
          if (response.hasError) {
            setShowError(true);
            setErrorMessage(response.customErrorMessage ?? '');
          } else {
            const getVerificationMethodResponse = response.value;
            getVerificationMethodResponse?.methods.push(VerificationMethod.CODE);
            setVerificationMethodsResponse(getVerificationMethodResponse);
            setVerificationDestinationMap(createVerificationMethodToDestinationMap(getVerificationMethodResponse));
            setVerificationMethodsLoaded(true);
          }
        }).catch(aborted => {
          if (!aborted) {
            console.log('Fetching possible verification methods failed.');
          }
        });
    }
    return () => abortController.abort();
  }, [verificationMethodsResponse, entity.id, externalId, groupId, linkedAccount, loadedVerificationStatus, showError]);

  const onRetry = () => {
    setShowError(false);
    setLoadedVerificationStatus(false);
    setAlreadyVerified(verifiedListing?.method === VerificationMethod.PREVIOUSLY_VERIFIED);
    setVerificationMethodsResponse(undefined);
    setVerificationDestinationMap(undefined);
    setVerificationMethodsLoaded(false);
  };

  return (
    <div className={styles.verificationContainer} key={entity.id}>
      <Divider />
      {showError
       ? <><ErrorCallout onRetry={onRetry} message={errorMessage}></ErrorCallout></>
       : <>
         <div className={styles.verificationReason}>
           <CalloutBanner type={CalloutBannerType.WARNING}>
             {yext.msg('If you do not verify your Google Business Profile listing, any changes you make to your listing will not be visible to your customers. If the location group that you are launching this location in is bulk verified, you can click skip verification. After that, please wait up to 72 hours for the listing to auto-verify on its own and transition to Synced.')}
           </CalloutBanner>
         </div>
         <SubHeader mainText={yext.msg('Your Entity')} />
         <EntityDetails entity={entity} />
         {verificationMethodsLoaded
        ? (
          <VerificationSelection
            alreadyVerified={alreadyVerified}
            verificationDestinationMap={verificationDestinationMap}
            setVerificationDestination={setVerificationDestination}
            verificationMethods={verificationMethodsResponse?.methods}
            usernameEditable={verificationMethodsResponse?.usernameEditable}
            entity={entity}
            externalId={externalId}
            groupId={groupId}
          />
        ) : <ContentSkeletonCards />}
       </>
      }
    </div>
  );
};

/**
 * @param {{
 *  alreadyVerified: boolean,
 *  verificationDestinationMap: Object,
 *  setVerificationDestination: function(VerificationMethod, string): void,
 *  verificationMethods: Array<VerificationMethod>,
 *  usernameEditable: boolean,
 *  entity: !EntityInfo,
 *  externalId: string,
 *  groupId: string,
 * }} props
 * @return {React.ReactNode}
 */
const VerificationSelection = ({
  alreadyVerified,
  verificationDestinationMap,
  setVerificationDestination,
  verificationMethods,
  usernameEditable,
  entity,
  externalId,
  groupId,
}) => {
  const {verifiedListings, setVerifiedListings} = React.useContext(ListingsLaunchContext);
  const [selectedVerificationMethod, setSelectedVerificationMethod] =
    React.useState(getVerificationForEntity(entity, verifiedListings)?.method ?? VerificationMethod.OTHER);
  const [alreadyHasVerificationCode, setAlreadyHasVerificationCode] = React.useState(false);
  const [shouldSkipVerification, setShouldSkipVerification] = React.useState(false);

  const isSmallViewport = useIsSmallViewport();

  const getOtherVerifiedListings = currentVerifiedListings => (
    currentVerifiedListings.filter(verification => verification.entityId !== entity.id)
  );

  const handleAlreadyVerified = () => {
    setSelectedVerificationMethod(VerificationMethod.PREVIOUSLY_VERIFIED);

    const newVerification = new VerifiedListing({
      entityId: entity.id,
      method: VerificationMethod.PREVIOUSLY_VERIFIED,
    });
    setAlreadyHasVerificationCode(false);
    setShouldSkipVerification(true);
    setVerifiedListings(currentVerifiedListings => [
      ...getOtherVerifiedListings(currentVerifiedListings),
      newVerification,
    ]);
  };

  if (alreadyVerified) {
    if (!shouldSkipVerification) {
      handleAlreadyVerified();
    }
    return <AlreadyVerifiedMessage />;
  }

  const codeVerificationSelected = VerificationMethod.CODE === selectedVerificationMethod;
  const verificationMethodName = codeVerificationSelected
    ? ''
    : getVerificationMethodDisplayName(selectedVerificationMethod);

  const getFlowContent = method => {
    if (shouldSkipVerification) {
      return (<SkipVerification />);
    } else {
      return (
        <VerificationFlow
          entity={entity}
          externalId={externalId}
          groupId={groupId}
          verificationMethod={method}
          verificationDestination={verificationDestinationMap[method]}
          setVerificationDestination={setVerificationDestination}
          usernameEditable={usernameEditable}
          onDidSend={
            () => {
              setAlreadyHasVerificationCode(true);

              // If the user has launched the VIDEO tab, we need to update the verification states
              // in the context. For other methods, this is done once a code is actually input,
              // but this method has no further steps and should allow the user to continue
              // immediately. Otherwise, we should unset this verification state as the user
              // probably still needs to enter a code.
              if (method === VerificationMethod.VIDEO) {
                const newVerification = new VerifiedListing({
                  entityId: entity.id,
                  method: method,
                });

                setVerifiedListings(currentVerifiedListings => [
                  ...getOtherVerifiedListings(currentVerifiedListings),
                  newVerification,
                ]);
              } else {
                setVerifiedListings(getOtherVerifiedListings);
              }
            }
          }
        />
      );
    }
  };

  const resetMethod = () => {
    setAlreadyHasVerificationCode(false);
    setSelectedVerificationMethod(VerificationMethod.OTHER);
    setVerifiedListings(getOtherVerifiedListings);
  };

  const onSwitchDestinationForMethod = method => {
    setAlreadyHasVerificationCode(false);
    setSelectedVerificationMethod(method);
    setVerifiedListings(getOtherVerifiedListings);
  };

  return (
    <div>
      {(!isSmallViewport || !alreadyHasVerificationCode) && (
        <VerificationMethods
          entity={entity}
          verificationMethods={verificationMethods}
          selectedVerificationMethod={selectedVerificationMethod}
          setSelectedVerificationMethod={setSelectedVerificationMethod}
          setAlreadyHasVerificationCode={setAlreadyHasVerificationCode}
          setShouldSkipVerification={setShouldSkipVerification}
          renderFlowContent={getFlowContent}
        />
      )}
      {(verificationMethodName || codeVerificationSelected)
        && (!isSmallViewport || alreadyHasVerificationCode)
        && (
          <strong className={styles.subTitleText}>
            <div className={styles.verificationMethod}>
              {isSmallViewport
                && (<div className={styles.verificationMethodIcon}>
                  {getIconForVerificationMethod(selectedVerificationMethod)}
                </div>)}
              <div>
                {yext.msg('{0} Verification', verificationMethodName)}
              </div>
            </div>
          </strong>
        )}
      {!alreadyHasVerificationCode ? (
        !isSmallViewport && getFlowContent(selectedVerificationMethod)
      ) : (
        <VerificationCodeEntry
          entityId={entity.id}
          externalId={externalId}
          groupId={groupId}
          verificationMethod={selectedVerificationMethod}
          verificationDestination={verificationDestinationMap[selectedVerificationMethod]}
          onCancel={resetMethod}
          canSwitchDestination={usernameEditable && selectedVerificationMethod === VerificationMethod.EMAIL}
          onSwitchDestinationForMethod={onSwitchDestinationForMethod}
        />
      )}
    </div>
  );
};

const AlreadyVerifiedMessage = () => {
  return (
    <div className={styles.subTitleText}>
      <strong>{yext.msg('Verification')}</strong>
      <p className={styles.successLabel}>{yext.msg('Your entity was previously verified! No actions are needed on your behalf.')}</p>
    </div>
  );
};

/**
 * @return {React.ReactNode}
 */
const SkipVerification = () => {
  return (
    <div>
      <p>{yext.msg('This will skip verifying your identity for Google Business Profile. Continue through the rest of this process.')}</p>
    </div>
  );
};

export default VerifyEntity;
